import {
  scopeStorageKey,
  type DepartmentFilterValue,
  type LocationFilterValue,
  type MeAccessScopeResponse,
  type ScopeFilterParams,
} from "../../../../shared/types/accessScope";
import { DEPARTMENT_FILTER_ENABLED } from "./departmentFilter";

/**
 * The Location and Department filters' rules, without React.
 *
 * Every function is pure (storage is passed in), so each rule the plan sets is a
 * unit test: which dropdown shows, what "All" is called, which saved value
 * survives, and what the server is sent.
 */

export const ALL = "all" as const;

/** The filters exist only for roles whose data is scoped by community (D10). */
const FILTERED_DATA_SCOPES = new Set(["all", "communities", "assigned_jobs"]);

export function scopeFiltersApply(scope: MeAccessScopeResponse | undefined): boolean {
  return !!scope && FILTERED_DATA_SCOPES.has(scope.dataScope);
}

/**
 * Whether the caller reaches every community and department of the instance: the
 * server's `requireUnrestricted` rule (scopeSql `reachesWholeInstance`, D29), for
 * actions that walk the whole instance.
 */
export function reachesWholeInstance(scope: MeAccessScopeResponse | undefined): boolean {
  if (!scope) return false;
  if (scope.dataScope === "all") return true;
  return (
    scope.dataScope === "communities" &&
    scope.locations.unrestricted &&
    scope.departments.unrestricted
  );
}

/**
 * Whether this person's access reaches NOTHING: a role scoped by community, restricted to an
 * empty set of them.
 *
 * Who that is: the "not set up yet" person, given a location-limited role with no locations
 * decided yet, whom the server denies on purpose until someone chooses (`accessScope.ts`'s
 * pending flag, "late access beats leaked access"). Also anyone whose only group has lost its
 * communities, and anyone whose communities are all deactivated.
 *
 * They see no jobs and no candidates, and the server refuses to place a new job for them
 * (`assertJobPlacementInScope`, 403), so Add Job Posting, Add Applicant and Bulk Upload are
 * offered but cannot finish: QA filled the job form and lost the work at Save (AC-02). The
 * buttons stay, disabled, with the reason -- hiding them tells an admin the feature is gone.
 */
export function reachesNothing(scope: MeAccessScopeResponse | undefined): boolean {
  if (!scope || !FILTERED_DATA_SCOPES.has(scope.dataScope)) return false;
  return !scope.locations.unrestricted && scope.locations.options.length === 0;
}

/** Why those actions are refused, for a tooltip beside the disabled button. */
export const NO_ACCESS_YET_REASON =
  "You don't have access to any location yet, so this can't be saved. Ask your admin to set your locations and departments.";

/** Hidden when there is nothing to choose between (a user with one community, D8). */
export function showLocationDropdown(scope: MeAccessScopeResponse | undefined): boolean {
  return scopeFiltersApply(scope) && scope!.locations.options.length > 1;
}

/**
 * Shown only when the instance uses departments (D17); hidden for a user
 * restricted to a single department (D8).
 */
export function showDepartmentDropdown(scope: MeAccessScopeResponse | undefined): boolean {
  // Switched off, this also makes validDepartment answer All, so a department in
  // the URL or remembered from before is ignored: nothing stays filtered with no
  // control on screen.
  if (!DEPARTMENT_FILTER_ENABLED) return false;
  if (!scopeFiltersApply(scope) || !scope!.departments.enabled) return false;
  return scope!.departments.unrestricted || scope!.departments.options.length > 1;
}

// ── Parsing and validation ────────────────────────────────────────────────────

const LOCATION_VALUE = /^location:([1-9]\d*)$/;
const GROUP_VALUE = /^group:(.+)$/;

/** Shape only. Whether the value is one the caller may pick is `validLocation`'s job. */
export function parseLocationValue(raw: string | null | undefined): LocationFilterValue | null {
  if (!raw) return null;
  if (raw === ALL) return ALL;
  const location = LOCATION_VALUE.exec(raw);
  if (location) return `location:${Number(location[1])}`;
  const group = GROUP_VALUE.exec(raw);
  if (group && group[1].trim()) return `group:${group[1]}`;
  return null;
}

export function parseDepartmentValue(raw: string | null | undefined): DepartmentFilterValue | null {
  if (!raw || !raw.trim()) return null;
  return raw;
}

/** A value the dropdown can show, or "all". Anything stale or hidden falls back silently. */
export function validLocation(
  value: LocationFilterValue | null,
  scope: MeAccessScopeResponse | undefined,
): LocationFilterValue {
  if (!value || value === ALL || !showLocationDropdown(scope)) return ALL;
  if (value.startsWith("location:")) {
    const id = Number(value.slice("location:".length));
    return scope!.locations.options.some((option) => option.id === id) ? value : ALL;
  }
  const id = value.slice("group:".length);
  return scope!.groups.some((group) => group.id === id) ? value : ALL;
}

export function validDepartment(
  value: DepartmentFilterValue | null,
  scope: MeAccessScopeResponse | undefined,
): DepartmentFilterValue {
  if (!value || value === ALL || !showDepartmentDropdown(scope)) return ALL;
  return scope!.departments.options.some((option) => option.id === value) ? value : ALL;
}

// ── Remembered choice (per instance) ─────────────────────────────────────────

export interface StoredScope {
  loc?: string;
  dept?: string;
}

/** Last choice for this instance. Blocked or corrupt storage reads as nothing remembered. */
export function readStoredScope(instanceId: string, storage: Storage | undefined): StoredScope {
  if (!instanceId || !storage) return {};
  try {
    const raw = storage.getItem(scopeStorageKey(instanceId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const { loc, dept } = parsed as Record<string, unknown>;
    return {
      ...(typeof loc === "string" ? { loc } : {}),
      ...(typeof dept === "string" ? { dept } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * Best effort: a viewer convenience, never a reason to fail the page.
 *
 * Change one remembered value and keep the other. The header owns `loc` and the
 * page filter row owns `dept`, so neither may overwrite the other's. `undefined`
 * forgets that value.
 */
export function updateStoredScope(
  instanceId: string,
  patch: StoredScope,
  storage: Storage | undefined,
): void {
  if (!instanceId || !storage) return;
  const next: StoredScope = { ...readStoredScope(instanceId, storage) };
  for (const key of ["loc", "dept"] as const) {
    if (!(key in patch)) continue;
    const value = patch[key];
    if (value === undefined || value === ALL) delete next[key];
    else next[key] = value;
  }
  try {
    if (next.loc === undefined && next.dept === undefined) {
      storage.removeItem(scopeStorageKey(instanceId));
    } else {
      storage.setItem(scopeStorageKey(instanceId), JSON.stringify(next));
    }
  } catch {
    // Private mode, quota, blocked site data: the choice simply isn't remembered.
  }
}

/**
 * The effective choice for each dropdown: the URL wins, then this instance's
 * remembered choice, then "all". Each is validated against the options.
 */
export function resolveSelection(input: {
  urlLocation: string | null;
  urlDepartment: string | null;
  stored: StoredScope;
  scope: MeAccessScopeResponse | undefined;
}): { location: LocationFilterValue; department: DepartmentFilterValue } {
  const { urlLocation, urlDepartment, stored, scope } = input;
  return {
    location: validLocation(parseLocationValue(urlLocation ?? stored.loc), scope),
    department: validDepartment(parseDepartmentValue(urlDepartment ?? stored.dept), scope),
  };
}

/** What a scoped endpoint is sent. `locationId` and `groupId` are never both set. */
export function toScopeParams(
  location: LocationFilterValue,
  department: DepartmentFilterValue,
): ScopeFilterParams {
  const params: ScopeFilterParams = {};
  if (location.startsWith("location:")) {
    params.locationId = Number(location.slice("location:".length));
  } else if (location.startsWith("group:")) {
    params.groupId = location.slice("group:".length);
  }
  if (DEPARTMENT_FILTER_ENABLED && department !== ALL) params.departmentId = department;
  return params;
}

// ── Options ──────────────────────────────────────────────────────────────────

export interface ScopeDropdownOption {
  value: string;
  text: string;
}

export interface ScopeDropdownGroup {
  label: string;
  options: ScopeDropdownOption[];
}

/**
 * One flat list: "All …" first, then groups, then communities (D8, D36).
 *
 * No section headers: three headers over a handful of rows was most of the menu,
 * and a group row is told apart by its own mark rather than by a heading.
 */
export function locationOptions(scope: MeAccessScopeResponse): ScopeDropdownOption[] {
  return [
    { value: ALL, text: scope.locations.unrestricted ? "All locations" : "All my locations" },
    ...scope.groups.map((group) => ({ value: `group:${group.id}`, text: group.name })),
    ...scope.locations.options.map((location) => ({
      value: `location:${location.id}`,
      text: location.name,
    })),
  ];
}

/** Whether a value from `locationOptions` is a group (which the menu marks). */
export const isGroupOption = (value: string) => value.startsWith("group:");

export function departmentOptions(scope: MeAccessScopeResponse): ScopeDropdownOption[] {
  return [
    {
      value: ALL,
      text: scope.departments.unrestricted ? "All departments" : "All my departments",
    },
    ...scope.departments.options.map((department) => ({
      value: department.id,
      text: department.label,
    })),
  ];
}

/** The chosen option's words, for a printed header or a summary line. Null for "all". */
export function selectionLabels(
  scope: MeAccessScopeResponse | undefined,
  location: LocationFilterValue,
  department: DepartmentFilterValue,
): { location: string | null; department: string | null } {
  if (!scope) return { location: null, department: null };
  const locationText =
    location === ALL
      ? null
      : (locationOptions(scope).find((option) => option.value === location)?.text ?? null);
  const departmentText =
    department === ALL
      ? null
      : (scope.departments.options.find((option) => option.id === department)?.label ?? null);
  return { location: locationText, department: departmentText };
}
